import nanoid from 'nanoid';
import moment from 'moment';
import uniqBy from 'lodash/uniqBy';
import { store } from './../store';
import BaseSubscription from './BaseSubscription';
import {
  insertGraphQLFragments,
  simplifyGraphQLJSON,
  sendAPIRequest,
  gql,
} from './GraphQLClient';

import services, { logInfo, logError } from './services';
import { setCurrentMessages } from './../actions/actions';
import { fragmentMessageFull, serializeMessageFull } from './serializers/serializeMessage';


export const MESSAGES_CHANGE = 'MESSAGES_CHANGE';
export const MESSAGES_RECEIVE = 'MESSAGES_RECEIVE';


export default class MessageSubscription extends BaseSubscription {

  MESSAGE_HISTORY_REQUEST_INTERVAL = 30 * 1000;

  currentRoomMessageHistory = [];
  temporaryMessageTempIds = [];
  reachedBeginningOfMessageHistory = false;
  reachedEndingOfMessageHistory = true;
  messageHistoryRequestInterval = null;

  companyMessageSubscription = insertGraphQLFragments(gql`
    subscription ($companyId: Uuid!) {
      newCompanyMessage(companyId: $companyId) {
        ...fragmentMessageFull
      }
    }
  `, { fragmentMessageFull });

  companyMessageQuery = insertGraphQLFragments(gql`
    query($companyId: Uuid!, $afterTimestamp: DateTime) {
      companyMessages(companyId: $companyId, afterTimestamp: $afterTimestamp, onlyAssigned: false, first: 100) {
        edges {
          node {
            ...fragmentMessageFull
          }
        }
      }
    }
  `, { fragmentMessageFull });

  sendMessageQuery = insertGraphQLFragments(gql`
    mutation ($roomId: ID!, $text: String!, $responseToMessageId: ID, $attachments: [Upload!], $tempId: ID, $mentionedUserIds: [String!], $mustOpenWidget: Boolean) {
      sendMessage (roomId: $roomId, text: $text, responseToMessageId: $responseToMessageId, attachments: $attachments, tempId: $tempId, mentions: $mentionedUserIds, mustOpenWidget: $mustOpenWidget) {
        ...fragmentMessageFull
      }
    }
  `, { fragmentMessageFull });

  editMessageQuery = insertGraphQLFragments(gql`
    mutation ($messageId: ID!, $text: String!, $responseToMessageId: ID, $attachments: [Upload], $mentionedUserIds: [String!], $mustOpenWidget: Boolean) {
      editMessage (messageId: $messageId, text: $text, responseToMessageId: $responseToMessageId, attachments: $attachments, mentions: $mentionedUserIds, mustOpenWidget: $mustOpenWidget) {
        ...fragmentMessageFull
      }
    }
  `, { fragmentMessageFull });

  deleteMessageQuery = gql`
    mutation ($messageId: ID!) {
      deleteMessage (messageId: $messageId) {
        id
      }
    }
  `;

  messageHistoryQuery = insertGraphQLFragments(gql`
    query ($roomId: ID!, $first: Int, $last: Int, $before: String, $after: String) {
      messages(roomId: $roomId, first: $first, last: $last, before: $before, after: $after) {
        edges {
          cursor
          node {
            ...fragmentMessageFull
          }
        }
        pageInfo {
          hasNextPage
          hasPreviousPage
        }
      }
    }
  `, { fragmentMessageFull });

  
  messageSearchQuery = insertGraphQLFragments(gql`
    query ($roomId: id!, $searchTerm: String!, $limit: Int!, $afterCursor: String) {
      searchMessagesByRoom(roomId: $roomId, query: $searchTerm, first: $limit, after: $afterCursor) {
        edges {
          cursor
          node {
            id
          }
        }
      }
    }
  `);
  
  messageHistorySearchQuery = insertGraphQLFragments(gql`
    query($roomId: ID!, $messageId: ID!, $beforeAmount: Int, $afterAmount: Int) {
      message_with_page(
      room_id: $roomId,
      message_id: $messageId,
      before_amount: $beforeAmount,
      after_amount: $afterAmount ) {
        edges {
          cursor
          node {
            ...fragmentMessageFull
          }
        }
        pageInfo {
          hasNextPage
          hasPreviousPage
        }
      }
    }
  `, { fragmentMessageFull });

  subscribe = (companyId) => {
    this.updateMessageHistoryOnInterval();
    if (this.companyId === companyId) {
      return;
    }
    return services.graphQLClientSocket.subscribe({
      query: this.companyMessageSubscription,
      variables: { companyId },
      onAbort: error => {
        logError('MessageSubscription: Failed to subscribe to messages', { error });
      },
      onStart: () => {
        logInfo('MessageSubscription: Successfully subscribed to messages');
      },
      onResult: response => {
        const data = response?.data?.newCompanyMessage;
        if (!data) {
          return;
        }
        const message = serializeMessageFull(data);
        if (this.temporaryMessageTempIds.includes(message.tempId)) {
          this.forgetTemporaryMessage(message.tempId);
        }
        else {
          this.onMessageReceive(message);
        }
      },
    });
  };

  setRoom = (roomId) => {
    logInfo('MessageSubscription: Set room', { roomId });
    this.roomId = roomId;
    this.currentRoomMessageHistory = [];
    this.clearReachedState();
    this.setMessagesInRedux([]);
  };

  onMessageReceive = (message) => {
    const isMessageSentToCurrentRoom = message.room.id === this.roomId;
    if (isMessageSentToCurrentRoom) {
      const dedupedMessageHistory = uniqBy([...this.currentRoomMessageHistory, message], 'id');
      if (dedupedMessageHistory.length === this.currentRoomMessageHistory.length) {
        return;
      }
      this.currentRoomMessageHistory = dedupedMessageHistory;
      if (message.sender.isOperator) {
        this.currentRoomMessageHistory = this.markMessagesAsRead(this.currentRoomMessageHistory);
      }
      if (message.systemData.type === 'CaseClosedMessage') {
        this.currentRoomMessageHistory = this.markMessagesAsCaseClosed(this.currentRoomMessageHistory);
      }
      this.setMessagesInRedux(this.currentRoomMessageHistory);
    }
    logInfo('MessageSubscription: Received new message', { message });
    this.triggerEvent(MESSAGES_RECEIVE, message);
  };

  serializeSendMessageParams = (textareaParams) => {
    const tempId = textareaParams.tempId;
    const text = typeof textareaParams.text === 'string' ? textareaParams.text.trim() : '';
    const responseToMessageId = typeof textareaParams.responseToMessageId === 'string' ? textareaParams.responseToMessageId : null;
    const mentionedUserIds = textareaParams.mentionedUserIds instanceof Array ? textareaParams.mentionedUserIds : [];
    const mustOpenWidget = Boolean(textareaParams.mustOpenWidget) || false;
    const attachments = [];
    const binaries = {};

    try {
      textareaParams.attachments.forEach(({ file }) => {
        attachments.push(file.name);
        binaries[file.name] = file;
      });
    }
    catch (e) {}

    return {
      variables: {
        tempId,
        text,
        attachments,
        responseToMessageId,
        mentionedUserIds,
        mustOpenWidget,
        roomId: this.roomId,
      },
      binaries,
    };
  };

  serializeEditMessageParams = (textareaParams, originalMessage, didAttachmentChange) => {
    const text = typeof textareaParams.text === 'string' ? textareaParams.text.trim() : '';
    const responseToMessageId = typeof textareaParams.responseToMessageId === 'string' ? textareaParams.responseToMessageId : null;
    const mentionedUserIds = textareaParams.mentionedUserIds instanceof Array ? textareaParams.mentionedUserIds : [];
    const mustOpenWidget = Boolean(textareaParams.mustOpenWidget) || false;
    const messageId = originalMessage.id;

    let attachments = null;
    let binaries = {};

    if (didAttachmentChange) {
      attachments = [];
      try {
        textareaParams.attachments.forEach(({ file }) => {
          attachments.push(file.name);
          binaries[file.name] = file;
        });
      }
      catch (e) {}
    }
    return {
      variables: {
        messageId,
        text,
        attachments,
        responseToMessageId,
        mentionedUserIds,
        mustOpenWidget,
      },
      binaries,
    };
  };

  generateTemporaryMessage = (textareaParams, customData) => {
    const { currentOperator, currentRoom } = store.getState();
    const { text, attachments, mentionedUserIds, responseToMessageId, mustOpenWidget } = textareaParams;
    const timestamp = new Date().toISOString();

    return {
      ...serializeMessageFull({}),
      id: nanoid(),
      text: text.trim() || '',
      timestamp,
      mustOpenWidget,
      sender: currentOperator,
      room: {
        ...currentRoom,
        lastMessageAt: timestamp,
        unreadMessagesCount: 0,
      },
      isUnread: false,
      isCaseOpen: false,
      isSubmitting: true,
      mentions: this.generateTemporaryMessageMentions(mentionedUserIds),
      attachments: this.generateTemporaryMessageAttachments(attachments),
      responseToMessage: this.generateTemporaryMessageResponseToMessage(responseToMessageId),
      linkedIssues: [],
      ...customData,
    };
  };

  generateTemporaryMessageMentions = (mentionedUserIds) => {
    const { currentRoom } = store.getState();
    return mentionedUserIds.map(userId => {
      return {
        value: userId,
        client: currentRoom.members.filter(member => member.id === userId)[0]
      };
    });
  };

  generateTemporaryMessageResponseToMessage = (responseToMessageId) => {
    return this.currentRoomMessageHistory.filter(message => {
      return message.id === responseToMessageId;
    })[0] || {};
  };

  generateTemporaryMessageAttachments = (attachments) => {
    return attachments.map(attachment => {
      const { file, width, height, thumbnailUrl } = attachment;
      const contentType = file.type;
      const name = file.name;
      const url = URL.createObjectURL(file);
      let thumbnails = [];

      if (thumbnailUrl && width && height) {
        thumbnails = [{ id: attachment.id, url: thumbnailUrl }];
      }
      return {
        ...attachment,
        name,
        url,
        thumbnails,
        contentType,
        bytesSize: file.size,
        status: 'UPLOADING',
      };
    });
  };

  appendMessageConditionally = (message) => {
    this.temporaryMessageTempIds.push(message.tempId);
    logInfo('MessageSubscription: Conditionally appended message', { message });
    this.onMessageReceive(message);
  };

  changeMessageBy = (query, diff) => {
    const updatedMessageHistory = this.currentRoomMessageHistory.map(message => {
      let updatedMessage = { ...message };
      if ( this.doesMessageMatchQuery(message, query) ) {
        updatedMessage = {
          ...updatedMessage,
          ...diff,
        };
      }
      if (message.responseToMessage?.id && this.doesMessageMatchQuery(message.responseToMessage, query)) {
        updatedMessage.responseToMessage = {
          ...updatedMessage.responseToMessage,
          ...diff,
        };
      }
      return updatedMessage;
    });
    this.onMessageHistoryChange(updatedMessageHistory);
  };

  doesMessageMatchQuery = (message, query) => {
    for (let queryKey in query) {
      if (query[queryKey] !== message[queryKey]) {
        return false;
      }
    }
    return true;
  };

  forgetTemporaryMessage = (temporaryMessageTempId) => {
    this.temporaryMessageTempIds = this.temporaryMessageTempIds.filter(id => id !== temporaryMessageTempId);
  };

  setMessagesInRedux = (messages) => {
    store.dispatch(setCurrentMessages(messages));
  };

  editMessage = (textareaParams, originalMessage, didAttachmentChange) => {
    const { variables, binaries } = this.serializeEditMessageParams(textareaParams, originalMessage, didAttachmentChange);
    const messageId = originalMessage.id;
    let attachments = originalMessage.attachments;
    if (didAttachmentChange) {
      attachments = this.generateTemporaryMessageAttachments(textareaParams.attachments);
    }

    this.changeMessageBy({ id: messageId }, {
      attachments,
      text: variables.text,
      mustOpenWidget: variables.mustOpenWidget,
      editedAt: new Date().toISOString(),
      responseToMessage: this.generateTemporaryMessageResponseToMessage(variables.responseToMessageId),
      mentions: this.generateTemporaryMessageMentions(variables.mentionedUserIds),
      isSubmitting: true,
    });

    return sendAPIRequest(this.editMessageQuery, variables, binaries)
      .then(data => {
        const editedMessage = serializeMessageFull(data);
        this.changeMessageBy({ id: editedMessage.id }, editedMessage);
        return editedMessage;
      })
      .catch(e => {
        const submissionErrorCode = this.getSubmissionErrorCode(e);
        this.changeMessageBy({ id: messageId }, {
          isSubmitting: false,
          submissionError: {
            code: submissionErrorCode,
            isEditing: true,
          },
        });
        throw e;
      });
  };

  retryEditMessage = (message) => {
    const textareaParams = {
      text: message.text,
      attachments: message.attachments,
      responseToMessageId: message.responseToMessage?.id,
      mentionedUserIds: message.mentions.map(mention => mention.value),
      mustOpenWidget: message.mustOpenWidget,
    };
    this.editMessage(textareaParams, message, true);
  };

  deleteMessage = (messageId) => {
    this.changeMessageBy({ id: messageId }, { isDeleted: true });
    return sendAPIRequest(this.deleteMessageQuery, { messageId });
  };

  /**
   * Send message
   *
   * @param {string}    textareaParams.text - message text
   * @param {Object[]}  textareaParams.attachments - list of File() instances to be attached to message
   * @param {string}    [textareaParams.responseToMessageId] - ID of the message to reply to
   * @param {string[]}  [textareaParams.mentionedUserIds] - list of user IDs to be mentioned in the message
   * @param {boolean}   [textareaParams.mustOpenWidget] - indicator whether the client's widget should forcefully open upon receiving this message
   *
   * @param {string}    [textareaParams.tempId] [ONLY USED INTERNALLY IN MessageSubscription.js] - temporary ID used for appendConditionally option
   * @param {boolean}   [textareaParams.appendConditionally] [ONLY USED INTERNALLY IN MessageSubscription.js] - appends message to message history BEFORE it's been actually submitted (suitable for sending large attachments)
   * @param {string}    [textareaParams.retrySubmissionByTempId] (ONLY USED INTERNALLY IN MessageSubscription.js) - retries sending message if it failed initially
   *
   * @returns {Promise<object>}
   */
  sendMessage = (textareaParams) => {
    const { variables, binaries } = this.serializeSendMessageParams(textareaParams);
    let tempId;

    if (!variables.text && !variables.attachments.length) {
      const errorMessage = 'MessageSubscription: Either "text" or "attachments" parameter must not be empty';
      logError(errorMessage, variables);
      return Promise.reject({ message: errorMessage });
    }
    else if (textareaParams.appendConditionally) {
      tempId = nanoid();
      variables.tempId = tempId;
      const temporaryMessage = this.generateTemporaryMessage(textareaParams, { tempId });
      this.appendMessageConditionally(temporaryMessage);
    }
    else if (textareaParams.retrySubmissionByTempId) {
      tempId = textareaParams.retrySubmissionByTempId;
      variables.tempId = tempId;
      this.changeMessageBy({ tempId }, {
        isSubmitting: true,
        submissionError: null,
      });
    }

    return sendAPIRequest(this.sendMessageQuery, variables, binaries)
      .then(data => {
        const sentMessage = serializeMessageFull(data);
        const { tempId } = sentMessage;
        if (tempId) {
          logInfo('MessageSubscription: Enriched temporary message with actual one', { sentMessage });
          this.changeMessageBy({ tempId }, sentMessage);
        }
        else {
          logInfo('MessageSubscription: Sent message', { textareaParams, variables, sentMessage });
        }
        return sentMessage;
      })
      .catch(e => {
        const submissionErrorCode = this.getSubmissionErrorCode(e);
        this.changeMessageBy({ tempId }, {
          isSubmitting: false,
          submissionError: {
            code: submissionErrorCode,
          },
        });
        throw e;
      });
  };

  retrySendMessage = (message) => {
    return this.sendMessage({
      text: message.text,
      attachments: message.attachments,
      responseToMessageId: message.responseToMessage?.id,
      mentionedUserIds: message.mentions.map(mention => mention.value),
      mustOpenWidget: message.mustOpenWidget,
      retrySubmissionByTempId: message.tempId,
    });
  };

  getSubmissionErrorCode = (e) => {
    const defaultErrorCode = 400;
    let submissionErrorCode = e.rawError?.errors?.[0]?.status || defaultErrorCode;
    if (!navigator.onLine) {
      submissionErrorCode = 503;
    }
    return submissionErrorCode;
  };

  getMessageHistoryByCursor = (params) => {
    const { limit, beforeCursor, afterCursor } = params || {};
    let variables;

    if (this.reachedBeginningOfMessageHistory && !beforeCursor && !this.reachedEndingOfMessageHistory && !afterCursor) {
      return Promise.resolve({
        processedMessageHistory: [],
        pageInfo: {}
      });
    }
    if (beforeCursor) {
      variables = {
        last: limit,
        before: beforeCursor,
        roomId: this.roomId,
      };
    }
    else if (afterCursor) {
      variables = {
        first: limit,
        after: afterCursor,
        roomId: this.roomId,
      };
    }
    else {
      variables = {
        last: limit,
        roomId: this.roomId,
      }
    }

    return sendAPIRequest(this.messageHistoryQuery, variables).then(messages => {
      return {
      processedMessageHistory: simplifyGraphQLJSON(messages).map(serializeMessageFull),
      pageInfo: messages.pageInfo
    }});
  };
  
  /**
   * Загрузка старых сообщений
   * @param limit
   * @returns {Promise<T>}
   */
  fetchMessageHistory = (limit) => {
    return this.getMessageHistoryByCursor({ limit }).then(result => {
      this.onMessageHistoryChange(result.processedMessageHistory);
      this.reachedBeginningOfMessageHistory = !result.pageInfo.hasPreviousPage;
      return result.processedMessageHistory;
    });
  };

  onMessageHistoryChange = (messageHistory) => {
    this.currentRoomMessageHistory = messageHistory;
    this.setMessagesInRedux(messageHistory);
    this.triggerEvent(MESSAGES_CHANGE, messageHistory);
  };

  updateMessageHistoryOnInterval = () => {
    this.messageHistoryRequestInterval = setInterval(() => {
      if (!this.reachedEndingOfMessageHistory) {
        return;
      }
      const { currentCompany } = store.getState();
      const latestUpdatedRoom = services.roomListSubscription?.getLatestUpdatedRoom?.();
      if (!latestUpdatedRoom) {
        return;
      }
      const latestMessageTimestamp = latestUpdatedRoom.lastMessageAt || latestUpdatedRoom.insertedAt;
      const afterTimestamp = moment(latestMessageTimestamp).add(1, 'seconds').toISOString();
      const variables = { companyId: currentCompany.id, afterTimestamp };

      return sendAPIRequest(this.companyMessageQuery, variables)
        .then(data => {
          const newMessages = simplifyGraphQLJSON(data);
          if (newMessages?.length) {
            newMessages.forEach(message => {
              this.onMessageReceive( serializeMessageFull(message) );
            });
          }
        })
        .catch(e => {
          e.overrideErrorNotification({ alert: false });
          throw e;
        });
    }, this.MESSAGE_HISTORY_REQUEST_INTERVAL);
  };

  fetchPrecedingMessageHistory = (limit) => {
    const latestCursor = this.currentRoomMessageHistory[0]?.cursor;
    if (!latestCursor) {
      const errorMessage = 'MessageSubscription: Failed to fetch previous message history - cursors not found';
      logError(errorMessage);
      return Promise.reject({ message: errorMessage });
    }
    const variables = { limit, beforeCursor: latestCursor };
    return this.getMessageHistoryByCursor(variables).then(result => {
      const { processedMessageHistory } = result;
      const extendedMessageHistory = uniqBy([...processedMessageHistory, ...this.currentRoomMessageHistory], 'id');
      this.onMessageHistoryChange(extendedMessageHistory);
      this.reachedBeginningOfMessageHistory = !result.pageInfo.hasPreviousPage;
      return extendedMessageHistory;
    });
  };
  
  /**
   * Загрузка новых сообщений
   * @param limit
   * @returns {Promise<never>|Promise<Array>}
   */
  fetchNextMessageHistory = (limit) => {
    const latestCursor = this.currentRoomMessageHistory[this.currentRoomMessageHistory.length - 1]?.cursor;
    if (!latestCursor) {
      const errorMessage = 'MessageSubscription: Failed to fetch next message history - cursors not found';
      logError(errorMessage);
      return Promise.reject({ message: errorMessage });
    }
    
    const variables = { limit, afterCursor: latestCursor };
    return this.getMessageHistoryByCursor(variables).then(result => {
      const { processedMessageHistory } = result;
      const extendedMessageHistory = uniqBy([...this.currentRoomMessageHistory, ...processedMessageHistory], 'id');
      this.onMessageHistoryChange(extendedMessageHistory);
      this.reachedEndingOfMessageHistory = !result.pageInfo.hasNextPage;
      return extendedMessageHistory;
    });
  };

  markMessagesAsRead = (messages) => {
    logInfo('MessageSubscription: Marked all messages as read');
    return messages.map(message => ({ ...message, isUnread: false }));
  };

  markMessagesAsCaseClosed = (messages) => {
    logInfo('MessageSubscription: Marked all messages as case closed');
    return messages.map(message => ({ ...message, isCaseOpen: false }));
  };

  unsubscribe = () => {
    this.temporaryMessageTempIds = [];
    this.currentRoomMessageHistory = [];
    this.reachedBeginningOfMessageHistory = false;
    this.setMessagesInRedux([]);
    clearInterval(this.messageHistoryRequestInterval);

    logInfo('MessageSubscription: Unsubscribing...');
    services.graphQLClientSocket.unsubscribe(this.companyMessageSubscription);
  };
  
  /**
   * Получение списка сообщение, в которых есть искомый текст
   * @param {String} searchText
   * @returns {Promise<*>}
   */
  getMessageBySearch = (searchText = '') => {
    let requestParams = {
      roomId: this.roomId,
      limit: 1000,
      searchTerm: searchText
    };
    
    
    return sendAPIRequest(this.messageSearchQuery, requestParams).then(messages => {
      return messages?.edges || [];
    });
  };
  
  /**
   * Получение части лога с предыдущим результатом поиска
   * @param messageId
   * @returns {Promise<*>}
   */
  loadPrevMessageHistory = (messageId) => {
    let requestParams = {
      roomId: this.roomId,
      afterAmount: 20,
      messageId
    };
    
    return sendAPIRequest(this.messageHistorySearchQuery, requestParams).then(newMessages => {
      const processedNewMessage = simplifyGraphQLJSON(newMessages).map(serializeMessageFull);
      delete requestParams.afterAmount;
      requestParams.beforeAmount = 20;
      return sendAPIRequest(this.messageHistorySearchQuery, requestParams).then(oldMessages => {
        const processedOldMessage = simplifyGraphQLJSON(oldMessages).map(serializeMessageFull);
        const newMessageHistory = uniqBy([...processedOldMessage, ...processedNewMessage], 'id');
        this.onMessageHistoryChange(newMessageHistory);
        this.reachedEndingOfMessageHistory = !newMessages.pageInfo.hasNextPage;
        return newMessages;
      });
    });
  };
  
  /**
   * Сброс флагов состояния информации о возможности подгрузки истории чата
   */
  clearReachedState = () => {
    this.reachedBeginningOfMessageHistory = false;
    this.reachedEndingOfMessageHistory = true;
  }
}
